import { testIdAttribute } from '@pocket/web-utilities/test-utils'
import { i18n } from 'common/utilities/i18n'
import PropTypes from 'prop-types'

// Titles to be used for each supported language
const languageTitles = {
  'de-DE': 'Deutsch',
  en: 'English',
  'es-ES': 'Español (de España)',
  'es-LA': 'Español (de América Latina)',
  'fr-CA': 'Français (Canada)',
  'fr-FR': 'Français (France)',
  'it-IT': 'Italiano',
  'ja-JP': '日本語',
  'ko-KR': '한국어',
  'nl-NL': 'Nederlands',
  'pl-PL': 'Polski',
  'pt-BR': 'Português (do Brasil)',
  'pt-PT': 'Português (Europeu)',
  'ru-RU': 'Русский',
  'zh-CN': '中文（簡體字）',
  'zh-TW': '正體中文 (繁體)'
}

/**
 * HreflangLinks
 * This adds appropriate tags for alternate available languages.
 * This is included in the main layout of the project so to use it
 * you simply need to pass in a `metaData` param that includes a `url`
 * to the layout component and `renderHreflangLinks` as a param.
 *
 * @param {string} url for page to share (required)
 */

export function HreflangLinks({ url, showCanonical }) {
  if (!url) return null

  // URL formatting
  const fullUrl = new URL(url)
  const origin = fullUrl.origin
  const path = fullUrl.pathname

  // Language related
  const { options } = i18n
  const allLanguages = options?.allLanguages
  const currentLanguage = allLanguages?.find(
    (language) => language === path.split('/')[1] // Searches for any supported language prefix
  )
  const genericPath = path.replace('/' + currentLanguage, '') // Removes language routing prefix

  return (
    <>
      {/* Canonical */}
      {showCanonical ? (
        <link
          rel="canonical"
          href={origin + '/' + currentLanguage + genericPath}
          {...testIdAttribute('link-canonical')}></link>
      ) : null}

      {/* Default language */}
      <link
        rel="alternate"
        hrefLang="x-default"
        href={origin + genericPath}
        {...testIdAttribute('hreflang-x-default')}></link>

      {/* List of available languages for this page */}
      {allLanguages.map((language) => (
        <link
          rel="alternate"
          hrefLang={language}
          href={origin + '/' + language + genericPath}
          title={languageTitles[language]}
          key={`${language}`}
          {...testIdAttribute(`hreflang-${language}`)}
        />
      ))}
    </>
  )
}

HreflangLinks.defaultProps = {
  showCanonical: true
}

HreflangLinks.propTypes = {
  url: PropTypes.string,
  showCanonical: PropTypes.bool
}
